import { useIsClient } from '#/hooks/useIsClient';
import { useI18n } from '#/i18n/hooks/useI18n';
import type {
  IanaTimezone,
  DateString,
  TimeString,
} from '#/types/TypedDateString';
import { humanDate } from '#/utils/string/datetime/dateString';
import { shortIanaTimezone } from '#/utils/string/datetime/ianaTimezone';
import { humanTime } from '#/utils/string/datetime/timeString';
import { getUserTimezone } from '#/utils/timezones';

import { ListingDateAndTimeTimezoneTooltip } from './ListingDateAndTimeTimezoneTooltip';

type Props = {
  date: DateString;
  time:
    | {
        type: 'single';
        value: TimeString;
        timezone: IanaTimezone;
      }
    | {
        type: 'range';
        start: TimeString;
        end: TimeString;
        timezone: IanaTimezone;
      }
    | null;
  qaId?: string;
};

export function ListingDateAndTime({ date, time, qaId }: Props) {
  const { locale } = useI18n();

  const isClient = useIsClient();
  const userTimezone = getUserTimezone();

  const timeString = (() => {
    if (!time) return null;

    if (time.type === 'single') return humanTime(time.value, { locale });

    const startTimeString = humanTime(time.start, { locale });
    const endTimeString = humanTime(time.end, { locale });
    return `${startTimeString} - ${endTimeString}`;
  })();

  const combinedDateTimeString = [
    humanDate(date, { locale, monthFormat: 'long' }),
    time && timeString,
  ]
    .filter(Boolean)
    .join(', ');

  const formattedDateTime = [
    combinedDateTimeString,
    time?.timezone && shortIanaTimezone(time.timezone, { date, locale }),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      data-qa-id={qaId}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <div>{formattedDateTime}</div>

      {time && isClient && userTimezone && userTimezone !== time.timezone && (
        <ListingDateAndTimeTimezoneTooltip
          date={date}
          startTime={time.type === 'single' ? time.value : time.start}
          endTime={time.type === 'range' ? time.end : null}
          fromTimezone={time.timezone}
          toTimezone={userTimezone}
        />
      )}
    </div>
  );
}
